import { useLocation, useNavigate } from 'react-router-dom';
import { isOwnerLicense, isProTier, isStarterTier, useLicense, useEntitlements } from '@shared/license';
import { useProfile } from '@shared/state';
import { BrandMark } from '@shared/ui';
import { useFabMenu } from './components/useFabMenu';
import { PrimaryIcon } from './PrimaryIcon';
import { ToolIcon } from './ToolIcon';
import { getExpertNavigationGroups, getPrimaryNavigationActiveId, getPrimaryNavigationItems, HUB_NAV_ITEMS, MOBILE_NAV_ITEMS, countTools } from './navigation';

type ShellNavVariant = 'bottom' | 'rail';

type ShellNavProps = {
  variant: ShellNavVariant;
};

export function ShellNav({ variant }: ShellNavProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const profile = useProfile();
  const license = useLicense();
  const { open, toggle } = useFabMenu();
  const mode = profile.mode || 'guided';
  const activeId = getPrimaryNavigationActiveId(location.pathname);
  const items = getPrimaryNavigationItems();
  const expertGroups = getExpertNavigationGroups();
  const isOwner = isOwnerLicense(license);
  const isRailExpert = variant === 'rail' && mode === 'expert';
  const isRailGuided = variant === 'rail' && mode === 'guided';
  const { isFeatureEnabled } = useEntitlements();

  const isRouteActive = (to: string) => location.pathname === to || location.pathname.startsWith(`${to}/`);

  const renderTool = (item: { to: string; label: string; icon: string; description: string; access?: string }) => {
    const isActive = isRouteActive(item.to);
    const navItem = HUB_NAV_ITEMS.find((ni) => ni.to === item.to);
    const featureKey = navItem ? `tool_${navItem.to.replace(/^\/tools\//, '').replace(/\//g, '_').replace(/-/g, '_')}` : null;
    const entitlementCheck = featureKey ? isFeatureEnabled(featureKey) : null;
    const locked = entitlementCheck !== null
      ? !entitlementCheck
      : item.access === 'starter'
        ? !isStarterTier(license.tier)
        : item.access === 'pro'
          ? !isProTier(license.tier)
          : false;
    const lockLabel = item.access === 'pro' ? 'Pro' : 'Starter';
    return (
      <button
        key={item.to}
        type="button"
        className={`desktop-sidebar-tool ${isActive ? 'active' : ''} ${locked ? 'locked' : ''}`}
        onClick={() => navigate(item.to)}
        aria-current={isActive ? 'page' : undefined}
        aria-label={locked ? `${item.label} verrouillé` : item.label}
        title={locked ? `${item.description} · Débloqué avec ${lockLabel}` : item.description}
      >
        <span className="desktop-sidebar-tool-icon" aria-hidden="true"><ToolIcon icon={item.icon} size={18} /></span>
        <span className="desktop-sidebar-tool-copy">
          <strong>{item.label}</strong>
          <span>{item.description}</span>
        </span>
        <span className="desktop-sidebar-tool-state" aria-hidden="true">
          {locked ? <><ToolIcon icon="lock" size={14} /> {lockLabel}</> : '→'}
        </span>
      </button>
    );
  };

  return (
    <nav
      className={`app-nav ${variant === 'bottom' ? 'bottomnav' : 'desktop-sidebar'} app-nav--${variant} app-nav--${mode} ${isRailGuided ? 'desktop-sidebar--guided' : ''} ${isRailExpert ? 'desktop-sidebar--expert' : ''}`}
      aria-label={isRailExpert ? 'Navigation expert' : 'Navigation principale'}
    >
      {isRailExpert ? (
        <div className="desktop-sidebar-inner">
          <div className="desktop-sidebar-brand">
            <BrandMark
              profile={profile}
              size="sm"
              className="desktop-sidebar-brandmark"
              label={profile.company || 'INDXONE'}
            />
            <div className="desktop-sidebar-brandcopy">
              <strong>{profile.company?.trim() || 'INDXONE Hub'}</strong>
              <span>Vos {countTools()} outils essentiels</span>
            </div>
          </div>

          <div className="desktop-sidebar-primary">
            {items.map((item) => {
              const isActive = activeId === item.id;
              const isAction = item.kind === 'action';
              return (
                <button
                  key={item.id}
                  type="button"
                  className={`desktop-sidebar-primary-item ${isActive && !isAction ? 'active' : ''} ${isAction ? 'is-action' : ''}`}
                  onClick={() => (isAction ? toggle('rail') : navigate(item.to))}
                  aria-current={isActive && !isAction ? 'page' : undefined}
                  aria-haspopup={isAction ? 'menu' : undefined}
                  aria-expanded={isAction ? open : undefined}
                  aria-label={item.label}
                  title={item.label}
                  data-fab-trigger={isAction ? 'true' : undefined}
                >
                  <span className="desktop-sidebar-primary-icon">
                    <PrimaryIcon id={item.id} size={20} />
                  </span>
                  <span className="desktop-sidebar-primary-label">{item.label}</span>
                </button>
              );
            })}
          </div>

          <div className="desktop-sidebar-groups">
            {expertGroups.map((group) => (
              <section key={group.id} className="desktop-sidebar-group">
                <div className="desktop-sidebar-group-head">
                  <span className="desktop-sidebar-group-icon" aria-hidden="true"><ToolIcon icon={group.icon} size={16} /></span>
                  <div>
                    <div className="desktop-sidebar-group-label">{group.label}</div>
                    <div className="desktop-sidebar-group-desc">{group.description}</div>
                  </div>
                </div>
                <div className="desktop-sidebar-group-items">
                  {group.items.filter((item) => item.access !== 'owner' || isOwner).map((item) => renderTool(item))}
                </div>
              </section>
            ))}
          </div>

          {license.tier === 'free' && (
            <div className="desktop-sidebar-activate">
              <button
                type="button"
                className="desktop-sidebar-activate-btn"
                onClick={() => navigate('/tools/activation')}
                title="Activer ma licence pour débloquer tous les outils"
              >
                <ToolIcon icon="lock" size={14} /> Activer ma licence
              </button>
            </div>
          )}
        </div>
      ) : variant === 'rail' ? (
        <div className="desktop-sidebar-inner">
          <div className="desktop-sidebar-brand">
            <BrandMark
              profile={profile}
              size="sm"
              className="desktop-sidebar-brandmark"
              label={profile.company || 'INDXONE'}
            />
            <div className="desktop-sidebar-brandcopy">
              <strong>{profile.company?.trim() || 'INDXONE Hub'}</strong>
              <span>Parcours guidé</span>
            </div>
          </div>

          <div className="desktop-sidebar-primary desktop-sidebar-primary--guided">
            {items.filter((item) => item.kind !== 'action').map((item) => {
                const isActive = activeId === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`desktop-sidebar-primary-item ${isActive ? 'active' : ''}`}
                    onClick={() => navigate(item.to)}
                    aria-current={isActive ? 'page' : undefined}
                    aria-label={item.label}
                    title={item.label}
                  >
                    <span className="desktop-sidebar-primary-icon"><PrimaryIcon id={item.id} size={20} /></span>
                    <span className="desktop-sidebar-primary-label">{item.label}</span>
                  </button>
                );
              })}
          </div>

          <div className="desktop-sidebar-primary desktop-sidebar-primary--end">
            {items
              .filter((item) => item.kind === 'action')
              .map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="desktop-sidebar-primary-item is-action"
                  onClick={() => toggle('rail')}
                  aria-haspopup="menu"
                  aria-expanded={open}
                  aria-label={item.label}
                  title={item.label}
                  data-fab-trigger="true"
                >
                  <span className="desktop-sidebar-primary-icon">
                    <PrimaryIcon id={item.id} size={20} />
                  </span>
                  <span className="desktop-sidebar-primary-label">{item.label}</span>
                </button>
              ))}
          </div>

          {license.tier === 'free' && (
            <div className="desktop-sidebar-activate">
              <button
                type="button"
                className="desktop-sidebar-activate-btn"
                onClick={() => navigate('/tools/activation')}
                title="Activer ma licence pour débloquer tous les outils"
              >
                <ToolIcon icon="lock" size={14} /> Activer ma licence
              </button>
            </div>
          )}
        </div>
      ) : (
        MOBILE_NAV_ITEMS.map((item) => {
          const isActive = activeId === item.id;
          const isAction = item.kind === 'action';
          return (
            <button
              key={item.id}
              type="button"
              className={`app-nav-item bottomnav-item ${isActive && !isAction ? 'active' : ''} ${isAction ? 'is-action' : ''}`}
              onClick={() => (isAction ? toggle('bottom') : navigate(item.to))}
              aria-current={isActive && !isAction ? 'page' : undefined}
              // Le déclencheur mobile "Créer" ouvre une feuille
              // `role="dialog"` (MobileBottomSheet), pas un menu — voir
              // FabMenu.tsx. Le rail desktop (ci-dessus) ouvre toujours un
              // vrai menu et garde `aria-haspopup="menu"`, inchangé.
              aria-haspopup={isAction ? 'dialog' : undefined}
              aria-expanded={isAction ? open : undefined}
              aria-label={item.mobileLabel || item.label}
              title={item.label}
              data-fab-trigger={isAction ? 'true' : undefined}
            >
              <span className="bottomnav-icon">
                <PrimaryIcon id={item.id} size={20} />
              </span>
              {variant === 'bottom' && (
                <span className="bottomnav-label">{item.mobileLabel || item.label}</span>
              )}
            </button>
          );
        })
      )}
    </nav>
  );
}
